/**
 * Hooks para branding do tenant — TH-55126 / M-branding.3.
 *
 * Backend (TH-55125):
 *   - GET    /api/tenants/{id}/branding              (estado atual)
 *   - PATCH  /api/tenants/{id}/branding              (cores)
 *   - POST   /api/tenants/{id}/branding/logo         (upload PNG/SVG, multipart)
 *
 * Limites (espelhar regra do backend tenant_branding service):
 *   - Tamanho ≤ 2 MB
 *   - MIME: image/png | image/svg+xml
 *   - PNG: dimensão mínima 256x256
 *
 * O cliente TS gerado ainda não conhece esses endpoints; usamos fetch
 * direto + credentials: "include" (padrão do admin — ver useUpdateMe).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface TenantBranding {
  tenant_id: string;
  logo_light_url: string | null;
  logo_dark_url: string | null;
  brand_primary: string | null;
  brand_secondary: string | null;
  contrast_warnings: string[];
}

export interface UpdateBrandingPayload {
  brand_primary?: string | null;
  brand_secondary?: string | null;
}

export type LogoVariant = "light" | "dark";

export const BRANDING_QUERY_KEY = ["tenant", "branding"] as const;

async function parseError(res: Response): Promise<Error> {
  const body = (await res.json().catch(() => ({}))) as { detail?: string };
  return new Error(body.detail ?? `HTTP ${res.status}`);
}

async function fetchBranding(tenantId: string): Promise<TenantBranding> {
  const res = await fetch(`/api/tenants/${tenantId}/branding`, {
    credentials: "include",
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as TenantBranding;
}

async function patchBranding(
  tenantId: string,
  payload: UpdateBrandingPayload,
): Promise<TenantBranding> {
  const res = await fetch(`/api/tenants/${tenantId}/branding`, {
    method: "PATCH",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as TenantBranding;
}

async function uploadLogo(
  tenantId: string,
  variant: LogoVariant,
  file: File,
): Promise<TenantBranding> {
  const fd = new FormData();
  fd.append("variant", variant);
  fd.append("file", file);
  const res = await fetch(`/api/tenants/${tenantId}/branding/logo`, {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as TenantBranding;
}

// ── Query ──────────────────────────────────────────────────────────────────

export function useTenantBranding(tenantId: string | null) {
  return useQuery({
    queryKey: [...BRANDING_QUERY_KEY, tenantId],
    queryFn: () => fetchBranding(tenantId as string),
    enabled: !!tenantId,
    staleTime: 30_000,
    retry: false,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────

export function useUpdateTenantBranding(tenantId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateBrandingPayload) =>
      patchBranding(tenantId as string, payload),
    onSuccess: (data) => {
      qc.setQueryData([...BRANDING_QUERY_KEY, tenantId], data);
    },
  });
}

export function useUploadTenantLogo(tenantId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ variant, file }: { variant: LogoVariant; file: File }) =>
      uploadLogo(tenantId as string, variant, file),
    onSuccess: (data) => {
      qc.setQueryData([...BRANDING_QUERY_KEY, tenantId], data);
    },
  });
}

// ── Helpers de validação client-side (espelha backend) ─────────────────────

export const LOGO_MAX_BYTES = 2 * 1024 * 1024;
export const LOGO_MIN_PNG_DIMENSION = 256;
export const LOGO_ALLOWED_MIME = ["image/png", "image/svg+xml"] as const;

const HEX_COLOR_RE = /^#[0-9a-f]{6}$/i;

export function isValidHexColor(value: string): boolean {
  return HEX_COLOR_RE.test(value);
}

export interface LogoValidationError {
  field: "size" | "mime" | "dimension";
  message: string;
}

/**
 * Validação rápida antes de subir. Backend ainda é fonte da verdade —
 * o handler de upload deve mostrar erro do backend se algo passar aqui.
 *
 * Para PNG, checa dimensão usando ImageBitmap (mais barato que
 * decode + canvas, suporte amplo). Para SVG, só valida tipo+tamanho —
 * dimensão fica a critério do backend.
 */
export async function validateLogoFile(
  file: File,
): Promise<LogoValidationError | null> {
  if (file.size > LOGO_MAX_BYTES) {
    return {
      field: "size",
      message: `Arquivo excede 2 MB (atual: ${(file.size / 1024 / 1024).toFixed(2)} MB).`,
    };
  }
  if (!LOGO_ALLOWED_MIME.includes(file.type as (typeof LOGO_ALLOWED_MIME)[number])) {
    return {
      field: "mime",
      message: "Formato não suportado. Envie PNG ou SVG.",
    };
  }
  if (file.type === "image/png") {
    try {
      const bitmap = await createImageBitmap(file);
      const { width, height } = bitmap;
      bitmap.close();
      if (width < LOGO_MIN_PNG_DIMENSION || height < LOGO_MIN_PNG_DIMENSION) {
        return {
          field: "dimension",
          message: `PNG precisa ter no mínimo ${LOGO_MIN_PNG_DIMENSION}×${LOGO_MIN_PNG_DIMENSION}px (atual: ${width}×${height}).`,
        };
      }
    } catch {
      // ImageBitmap pode falhar em ambientes de teste sem suporte; deixa
      // a validação final pro backend.
    }
  }
  return null;
}
